import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, firstValueFrom } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { UserService } from '../services/api/user.service';
import { AccessLevel, PermissionsService, RoutePermission } from '../services/permissions.service';
import { GoogleAuthService } from '../services/google.auth.service';


@Injectable({
  providedIn: 'root',
})
export class GaurdsGuard implements CanActivate {
  constructor(
    private afAuth: AngularFireAuth,
    private userService: UserService,
    private router: Router,
    private permissionsService: PermissionsService,
    private googleAuthService: GoogleAuthService
  ) {
    if (environment.production == false) {
      //use emulator
      //  afAuth.useEmulator('http://localhost:9099');
    }
  }
  
  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {

    console.log('🛡️ Auth Guard: Checking access for:', state.url);

    // Get permission config from route data
    const permissionConfig: RoutePermission = route.data['permissions'];
    const requiredAccess: AccessLevel = route.data['requiredAccess'] || AccessLevel.READ;


    // Don't guard the login route
    if (state.url === '/login') {
      console.log('🛡️ Auth Guard: Login route, allowing access');
      return true;
    }

    // Wait for Firebase auth state to be properly initialized
    // This ensures we check persisted sessions on page reload
    console.log('🛡️ Auth Guard: Waiting for Firebase Auth to initialize...');
    const user = await new Promise<any>((resolve) => {
      // Use a timeout to ensure we don't wait forever
      const timeout = setTimeout(() => {
        console.log('🛡️ Auth Guard: ⚠️ Auth check timeout after 5 seconds');
        resolve(null);
      }, 5000);

      // Subscribe to auth state - this properly waits for persisted sessions
      const subscription = this.afAuth.authState.subscribe((user) => {
        clearTimeout(timeout);
        subscription.unsubscribe();
        console.log('🛡️ Auth Guard: Auth state initialized:', user ? 'authenticated' : 'not authenticated');
        resolve(user);
      });
    });

    console.log('🛡️ Auth Guard: Firebase user:', user ? `${user.email} (${user.uid})` : 'not authenticated');

    if (user) {
      try {

        // Get tenant ID from Firebase Auth token claims (force refresh to ensure valid token)
        // Forcing refresh prevents "session expired" errors from stale tokens
        const tokenResult = await user.getIdTokenResult(true);
        const defaultTenantId = tokenResult.claims['tenantId'] as string;
        const availableTenants = tokenResult.claims['tenants'] as string[] || [];
        console.log('🛡️ Auth Guard: Default tenant from token:', defaultTenantId);
        console.log('🛡️ Auth Guard: Available tenants from token:', availableTenants);

        // Warn if permission claim is missing or unexpected — this causes Firestore rule failures
        const permissionClaim = tokenResult.claims['permission'];
        if (!permissionClaim) {
          console.error('🛡️ Auth Guard: ⚠️ WARNING: No "permission" claim in auth token! Firestore rules will deny access.');
        } else if (!['Super-Admin', 'Admin', 'Access'].includes(permissionClaim as string)) {
          console.error('🛡️ Auth Guard: ⚠️ WARNING: Unexpected permission claim value:', permissionClaim,
            '— expected "Super-Admin", "Admin", or "Access". Firestore rules may deny access.');
        }

        // Deep-link tenant override: ?t=TENANT_ID lets Slack/Teams/Moeba notifications
        // force a tenant switch when the user is logged into a different tenant.
        // The ?t value can arrive with a trailing path segment glued on
        // (e.g. ?t=<tenantId>/<cardKey>). Tenant IDs never contain a slash,
        // so take only the first segment before matching against the claims.
        const rawTenantParam = route.queryParamMap.get('t');
        const queryTenantId = rawTenantParam ? rawTenantParam.split('/')[0] : null;
        const storedTenantId = localStorage.getItem('tenantId');
        let tenantId: string;

        if (queryTenantId && availableTenants.includes(queryTenantId)) {
          tenantId = queryTenantId;
          console.log('🛡️ Auth Guard: Using tenant from ?t query param:', tenantId);
          if (storedTenantId !== tenantId) {
            localStorage.setItem('tenantId', tenantId);
          }
        } else if (queryTenantId && !availableTenants.includes(queryTenantId)) {
          console.warn('🛡️ Auth Guard: ?t tenant not in user availableTenants, ignoring:', queryTenantId);
          tenantId = (storedTenantId && availableTenants.includes(storedTenantId)) ? storedTenantId : defaultTenantId;
        } else if (storedTenantId && availableTenants.includes(storedTenantId)) {
          // User has switched to a valid tenant — respect their selection
          tenantId = storedTenantId;
          console.log('🛡️ Auth Guard: Using user-selected tenant from localStorage:', tenantId);
        } else if (defaultTenantId) {
          // Fall back to the default tenant from auth claims
          tenantId = defaultTenantId;
          console.log('🛡️ Auth Guard: Using default tenant from token:', tenantId);
          localStorage.setItem('tenantId', tenantId);
        } else {
          console.log('🛡️ Auth Guard: No tenant ID available, redirecting to login');
          localStorage.setItem('redirectUrl', state.url);
          return this.router.createUrlTree(['/login']);
        }

        // Check if user data is already loaded and tenant matches
        const currentUserLoaded = this.userService.currentUser &&
                                  this.userService.currentUser.key === user.uid &&
                                  this.userService.currentTenantId === tenantId;

        if (currentUserLoaded) {
          console.log('🛡️ Auth Guard: ✅ User data already loaded, skipping initialization');
          console.log('🛡️ Auth Guard: Current user:', this.userService.currentUser.email);
        } else {
          // Initialize tenant in service if not already initialized. Use the resilient
          // path: try the chosen tenant first, then fall back to other candidates, so a
          // single unreadable tenant (e.g. a stale active-tenant claim) doesn't hard-fail
          // the guard and bounce the user out. getTenantDetails self-heals a stale token.
          if (this.userService.currentTenantId !== tenantId) {
            console.log('🛡️ Auth Guard: Initializing tenant (with fallback):', tenantId);
            const lastSelected = await this.userService.getLastSelectedTenant(user.uid);
            const chosen = await this.userService.initializeFirstAvailableTenant([
              tenantId, storedTenantId, lastSelected, defaultTenantId, ...availableTenants,
            ]);
            if (!chosen) {
              console.error('🛡️ Auth Guard: ❌ No tenant could be loaded, redirecting to login');
              localStorage.setItem('redirectUrl', state.url);
              return this.router.createUrlTree(['/login']);
            }
            if (chosen !== tenantId) {
              tenantId = chosen;
              localStorage.setItem('tenantId', chosen);
            }
          } else {
            console.log('🛡️ Auth Guard: Tenant already initialized');
          }

          // Wait for user data to be loaded
          console.log('🛡️ Auth Guard: Waiting for user data...');
          console.log('🛡️ Auth Guard: Current user in service:', this.userService.currentUser);

          // Try to get user directly first with timeout
          try {
            const directUser = await Promise.race([
              firstValueFrom(this.userService.getItem(user.uid)),
              new Promise<null>((resolve) => setTimeout(() => {
                console.log('🛡️ Auth Guard: ⚠️ Direct user fetch timeout after 3 seconds');
                resolve(null);
              }, 3000))
            ]);

            console.log('🛡️ Auth Guard: Direct user fetch result:', directUser ? 'found' : 'not found');

            if (directUser) {
              console.log('🛡️ Auth Guard: Setting current user in service');
              this.userService.setCurrentUser(directUser);
            } else {
              console.error('🛡️ Auth Guard: ❌ User document not found in Firestore');
              console.error('🛡️ Auth Guard: ❌ Tenant ID:', tenantId);
              console.error('🛡️ Auth Guard: ❌ User UID:', user.uid);
              console.error('🛡️ Auth Guard: ❌ Expected path:', `${tenantId}/data/users/${user.uid}`);
            }
          } catch (directFetchError) {
            console.error('🛡️ Auth Guard: Error fetching user directly:', directFetchError);
          }

          // Wait for user observable with timeout
          const userData = await Promise.race([
            firstValueFrom(this.userService.currentUser$.pipe(first(u => !!u))),
            new Promise<null>((resolve) => setTimeout(() => {
              console.log('🛡️ Auth Guard: ⚠️ User observable timeout after 3 seconds');
              resolve(null);
            }, 3000))
          ]).catch(error => {
            console.error('🛡️ Auth Guard: Error getting user data from observable:', error);
            return null;
          });

          console.log('🛡️ Auth Guard: User data received:', userData ? 'yes' : 'no');

          if (!userData) {
            console.log('🛡️ Auth Guard: ❌ No user data, redirecting to login');
            localStorage.setItem('redirectUrl', state.url);
            return this.router.createUrlTree(['/login']);
          }

          // Ensure currentUser is properly set in userService
          if (!this.userService.currentUser) {
            console.error('🛡️ Auth Guard: ❌ Current user not set in userService, redirecting to login');
            localStorage.setItem('redirectUrl', state.url);
            return this.router.createUrlTree(['/login']);
          }

          console.log('🛡️ Auth Guard: ✅ Current user set:', this.userService.currentUser.email);
        }

        // If no permission config, allow access (but component can still restrict features)
        if (!permissionConfig) {
          console.log('🛡️ Auth Guard: No permission config, allowing access');
          return true;
        }

        // Check permission based on the user's role and required access level
        console.log('🛡️ Auth Guard: Checking permissions...');
        const hasAccess = await firstValueFrom(
          this.permissionsService.canAccess(permissionConfig, requiredAccess)
        );
        
        console.log('🛡️ Auth Guard: Has access:', hasAccess);
        
        if (!hasAccess) {
          console.log('🛡️ Auth Guard: Access denied, redirecting to dashboard');
          return this.router.createUrlTree(['/dashboard']);
        }
        
        console.log('🛡️ Auth Guard: Access granted');
        return true;
      } catch (error) {
        console.error('🛡️ Auth Guard: Error in guard:', error);
        localStorage.setItem('redirectUrl', state.url);
        return this.router.createUrlTree(['/login']);
      }
    } else {
      console.log('🛡️ Auth Guard: No Firebase user, redirecting to login');
      localStorage.setItem('redirectUrl', state.url);
      return this.router.createUrlTree(['/login']);
    }
  }
}
